import { createStubImplementation } from '@ardoq/stub-implementation';
import {
  APIComponentAttributes,
  APIComponentType,
  APIFieldAttributes,
  ArdoqId,
  LinkedComponent,
} from '@ardoq/api-types';
import {
  GetCssClassNamesOption,
  RepresentationData,
  type FieldValueScopeSource,
  type TextAreaFormat,
} from '@ardoq/data-model';
import { ReferenceFilterContext } from '@ardoq/filter-helpers';
export type GetSources = (componentId: ArdoqId) => LinkedComponent[];

export type GetTargets = (componentId: ArdoqId) => LinkedComponent[];

export type IsIncludedInContextByFilter = (
  component: APIComponentAttributes,
  ctx?: ReferenceFilterContext,
  scopeSource?: FieldValueScopeSource | null
) => boolean;

/**
 * @deprecated Each call resolves the component and its model type from global
 * state, and `resolveComponentType` rebuilds `componentTypesById` by flattening
 * the type tree every time. In hot paths (per-node loops) this is expensive.
 *
 * Prefer the pure scope-data version:
 * `getComponentDisplayColorAsSVGAttributes` from `@ardoq/scope-data`
 * (`componentUtils.ts`), which takes `EnhancedScopeData` + `FilterColorData`
 * and does no global lookups.
 */
export type GetComponentDisplayColorAsSVGAttributes = (
  componentId: ArdoqId,
  options: GetCssClassNamesOption
) => { fill?: string; stroke?: string };

export type GetCssClassNames = (
  componentId: ArdoqId,
  options?: GetCssClassNamesOption
) => string | null;

export type GetTypeName = (componentId: ArdoqId) => string | null;

export type GetType = (componentId: ArdoqId) => APIComponentType | null;

export type ResolveComponentType = (
  component: APIComponentAttributes
) => APIComponentType | null;

export type GetRepresentationData = (
  componentId: ArdoqId
) => RepresentationData | null;

export type GetChildren = (componentId: ArdoqId) => APIComponentAttributes[];

export type HasChildren = (componentId: ArdoqId) => boolean;

export type GetSubtree = (componentId: ArdoqId) => APIComponentAttributes[];

export type GetRootComponents = (
  workspaceId: ArdoqId
) => APIComponentAttributes[];

export type GetRawLabel = (componentId: ArdoqId) => string | null;

export type IsComponent = (componentId: ArdoqId) => boolean;

export type GetComponentColor = (componentId: ArdoqId) => string | null;

export type GetImage = (componentId: ArdoqId) => string | null;

export type GetShape = (componentId: ArdoqId) => string | null;

export type GetWorkspaceId = (componentId: ArdoqId) => ArdoqId | null;

export type GetFieldLabelAndValue = (
  componentId: ArdoqId,
  returnIfFormattingType?: boolean,
  textAreaFormat?: TextAreaFormat
) => string | null;

export type GetNameWithFieldLabelAndValue = (
  componentId: ArdoqId
) => string | null;

export type GetFields = (componentId: ArdoqId) => APIFieldAttributes[];

export type GetParentId = (componentId: ArdoqId) => ArdoqId | null;

export type GetLevel = (componentId: ArdoqId) => number;

export type IsExternallyManaged = (componentId: ArdoqId) => boolean;

export type IsExternallyMissing = (componentId: ArdoqId) => boolean;

export type IsScenarioContextComponent = (componentId: ArdoqId) => boolean;

export type GetName = (componentId: ArdoqId) => string | null;

export type GetFullPathName = (componentId: ArdoqId) => string | null;

export type Get = (componentId: ArdoqId) => APIComponentAttributes | null;

export type GetComponentsByWorkspaceId = (
  workspaceId: ArdoqId
) => APIComponentAttributes[];

export type CanComponentHaveChildren = (componentId: ArdoqId) => boolean;

export type GetPossibleParents = (
  componentId: ArdoqId
) => APIComponentAttributes[];

export type GetPossibleTypes = (
  componentId: ArdoqId
) => { val: ArdoqId; label: string }[];

export type HasWriteAccess = (componentId: ArdoqId) => boolean;

export type HasParentChanged = (componentId: ArdoqId) => boolean;
export type LocallyCreatedComponentAttributes = Omit<
  APIComponentAttributes,
  'component-key' | 'children'
>;
export type GetComponentAncetryIds = (
  componentId?: ArdoqId | null
) => ArdoqId[];

export type IsWorkspaceEmpty = (workspaceId: ArdoqId) => boolean;

export type GetComponentHierarchy = (
  componentId: ArdoqId,
  getChildrenFn?: (componentId: ArdoqId) => ArdoqId[]
) => Record<
  ArdoqId,
  {
    isFilteredOut: boolean;
    children?: ArdoqId[];
  }
>;

export type GetComponentsHierarchyByWorkspaceIds = (
  workspaceIds: ArdoqId[]
) => Record<
  ArdoqId,
  {
    isFilteredOut: boolean;
    children?: ArdoqId[];
  }
>;

/**
 * The intention is to allow to return the attributes of e.g. Backbone
 * collections directly without creating an actual copy first. The is
 * obviously more efficient, but also more dangerous. It requires that
 * the consumer understands this and only uses this method for local
 * access to data, never spreads this data further down to other consumers
 */
export type GetAllComponents = () => APIComponentAttributes[];

export type ComponentInterfaceImplementation = {
  getSources: GetSources;
  getTargets: GetTargets;
  isIncludedInContextByFilter: IsIncludedInContextByFilter;
  /** @deprecated prefer the pure scope-data version `getComponentDisplayColorAsSVGAttributes` from `@ardoq/scope-data` (`componentUtils.ts`) */
  getComponentDisplayColorAsSVGAttributes: GetComponentDisplayColorAsSVGAttributes;
  getCssClassNames: GetCssClassNames;
  getTypeName: GetTypeName;
  getType: GetType;
  resolveComponentType: ResolveComponentType;
  getRepresentationData: GetRepresentationData;
  getChildren: GetChildren;
  hasChildren: HasChildren;
  getSubtree: GetSubtree;
  getRootComponents: GetRootComponents;
  getRawLabel: GetRawLabel;
  isComponent: IsComponent;
  getComponentColor: GetComponentColor;
  getImage: GetImage;
  getShape: GetShape;
  getWorkspaceId: GetWorkspaceId;
  getFieldLabelAndValue: GetFieldLabelAndValue;
  getNameWithFieldLabelAndValue: GetNameWithFieldLabelAndValue;
  getFields: GetFields;
  getParentId: GetParentId;
  getLevel: GetLevel;
  isExternallyManaged: IsExternallyManaged;
  isExternallyMissing: IsExternallyMissing;
  isScenarioContextComponent: IsScenarioContextComponent;
  getDisplayName: GetName;
  getFullPathName: GetFullPathName;
  get: Get;
  getComponentsByWorkspaceId: GetComponentsByWorkspaceId;
  canComponentHaveChildren: CanComponentHaveChildren;
  getPossibleParents: GetPossibleParents;
  getPossibleTypes: GetPossibleTypes;
  hasParentChanged: HasParentChanged;
  getComponentAncestryIds: GetComponentAncetryIds;
  hasWriteAccess: HasWriteAccess;
  getAllComponents: GetAllComponents;
  isWorkspaceEmpty: IsWorkspaceEmpty;
  getComponentHierarchy: GetComponentHierarchy;
  getComponentsHierarchyByWorkspaceIds: GetComponentsHierarchyByWorkspaceIds;
};

const defaultImplementation: ComponentInterfaceImplementation = {
  getSources: () => [],
  getTargets: () => [],
  isIncludedInContextByFilter: () => true,
  getComponentDisplayColorAsSVGAttributes: () => ({ fill: '', stroke: '' }),
  getCssClassNames: () => null,
  getTypeName: () => null,
  getType: () => null,
  resolveComponentType: () => null,
  getRepresentationData: () => null,
  getChildren: () => [],
  hasChildren: () => false,
  getSubtree: () => [],
  getRootComponents: () => [],
  getRawLabel: () => null,
  isComponent: () => true,
  getComponentColor: () => null,
  getImage: () => null,
  getShape: () => null,
  getWorkspaceId: () => null,
  getFieldLabelAndValue: () => null,
  getNameWithFieldLabelAndValue: () => null,
  getFields: () => [],
  getParentId: () => null,
  getLevel: () => 1,
  isExternallyManaged: () => false,
  isExternallyMissing: () => false,
  isScenarioContextComponent: () => false,
  getDisplayName: () => null,
  getFullPathName: () => null,
  get: () => null,
  getComponentsByWorkspaceId: () => [],
  canComponentHaveChildren: () => false,
  getPossibleParents: () => [],
  getPossibleTypes: () => [],
  hasParentChanged: () => false,
  getComponentAncestryIds: () => [],
  hasWriteAccess: () => false,
  getAllComponents: () => [],
  isWorkspaceEmpty: () => false,
  getComponentHierarchy: () => ({}),
  getComponentsHierarchyByWorkspaceIds: () => ({}),
};

const {
  getSources,
  getTargets,
  isIncludedInContextByFilter,
  getComponentDisplayColorAsSVGAttributes,
  getCssClassNames,
  getTypeName,
  getType,
  resolveComponentType,
  getRepresentationData,
  getChildren,
  hasChildren,
  getSubtree,
  getRootComponents,
  getRawLabel,
  isComponent,
  getComponentColor,
  getImage,
  getShape,
  getWorkspaceId,
  getFieldLabelAndValue,
  getNameWithFieldLabelAndValue,
  getFields,
  getParentId,
  getLevel,
  isExternallyManaged,
  isExternallyMissing,
  isScenarioContextComponent,
  getDisplayName,
  getFullPathName,
  get,
  getComponentsByWorkspaceId,
  canComponentHaveChildren,
  getPossibleParents,
  getPossibleTypes,
  registerImplementation,
  hasParentChanged,
  getComponentAncestryIds,
  hasWriteAccess,
  getAllComponents,
  isWorkspaceEmpty,
  getComponentHierarchy,
  getComponentsHierarchyByWorkspaceIds,
} = createStubImplementation(
  '@ardoq/component-interface',
  defaultImplementation
);

/**
 * `componentInterface` is a Backbone-era singleton: every member resolves the
 * component, and often its model and the active filters, from global mutable
 * state at call time.
 *
 * The app is stream-based now. Resolve components from `components$`
 * (`streams/components/components$.ts`) at a permitted boundary -- a `*$.ts`
 * file, a file inside a `*$/` folder, or a `*routines.ts` -- and thread the
 * resolved data downstream as plain parameters.
 *
 * Most members here have a direct pure counterpart in `componentOperations`
 * (`@ardoq/core`), often under the same name -- `getCssClassNames`, `getColor`,
 * `getShape`, `getImage`, `getRawLabel`, `getRepresentationData`,
 * `canComponentHaveChildren`, `getPossibleParents`, `getRootComponents`,
 * `getFlatHierarchy`, `buildChildrenByParent`, `buildParentChangeCascade`. Type
 * resolution lives in `modelOperations` (`@ardoq/core`), the
 * `EnhancedScopeData` flavour in `componentUtils` /
 * `enhancedScopeDataOperations` (`@ardoq/scope-data`), and id extraction in
 * `entityOperations` (`toId` / `toIds` / `toIdSet`, `@ardoq/core`).
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const componentInterface = {
  defaultImplementation,
  registerImplementation,
  /**
   * @deprecated Join `graphModel$` (`modelInterface/graphModel$.ts`), or call `componentUtils.getSources(scopeData, componentId)` (`@ardoq/scope-data`).
   */
  getSources,
  /**
   * @deprecated Join `graphModel$` (`modelInterface/graphModel$.ts`), or call `componentUtils.getTargets(scopeData, componentId)` (`@ardoq/scope-data`).
   */
  getTargets,
  /**
   * @deprecated Join `includedInContext$` (`streams/filters/includedInContext$.ts`), or call the pure `isIncludedInContextByFilter` from `@ardoq/filter-helpers` with resolved filters.
   */
  isIncludedInContextByFilter,
  /** @deprecated prefer the pure scope-data version `getComponentDisplayColorAsSVGAttributes` from `@ardoq/scope-data` (`componentUtils.ts`) */
  getComponentDisplayColorAsSVGAttributes,
  /**
   * @deprecated Call `componentOperations.getCssClassNames(component, componentType, sanitizedFilterColor, options)` (`@ardoq/core`), or `componentUtils.getComponentCSSClassNames` (`@ardoq/scope-data`).
   */
  getCssClassNames,
  /**
   * @deprecated Call `enhancedScopeDataOperations.getComponentTypeByComponentId` (`@ardoq/scope-data`) and read `.name`.
   */
  getTypeName,
  /**
   * @deprecated Call `enhancedScopeDataOperations.getComponentTypeByComponentId` (`@ardoq/scope-data`), or `modelOperations.getComponentTypes(model).componentTypesById[typeId]` (`@ardoq/core`).
   */
  getType,
  /**
   * @deprecated Call `modelOperations.getComponentTypes(model)` (`@ardoq/core`) once per model emission; this member reflattens the type tree on every call.
   */
  resolveComponentType,
  /**
   * @deprecated Call `componentOperations.getRepresentationData(component, componentType)` (`@ardoq/core`).
   */
  getRepresentationData,
  /**
   * @deprecated Read `components$.childrenByParent`, which `components$` builds with `componentOperations.buildChildrenByParent` (`@ardoq/core`).
   */
  getChildren,
  /**
   * @deprecated Read `components$.childrenByParent`, which `components$` builds with `componentOperations.buildChildrenByParent` (`@ardoq/core`).
   */
  hasChildren,
  /**
   * @deprecated Call `componentOperations.getFlatHierarchy(roots, childrenByParent)` (`@ardoq/core`) with `components$.childrenByParent`.
   */
  getSubtree,
  /**
   * @deprecated Call `componentOperations.getRootComponents(workspaceId, components)` (`@ardoq/core`) with `components$.models`.
   */
  getRootComponents,
  /**
   * @deprecated Call `componentOperations.getRawLabel` (`@ardoq/core`) with the component resolved from `components$`.
   */
  getRawLabel,
  /**
   * @deprecated Read `components$.byId[id]` for presence, or call `componentUtils.isComponent` (`@ardoq/scope-data`).
   */
  isComponent,
  /**
   * @deprecated Call `componentOperations.getColor(component, componentType)` (`@ardoq/core`).
   */
  getComponentColor,
  /**
   * @deprecated Call `componentOperations.getImage(component, componentType)` (`@ardoq/core`).
   */
  getImage,
  /**
   * @deprecated Call `componentOperations.getShape(component, componentType)` (`@ardoq/core`).
   */
  getShape,
  /**
   * @deprecated Read `components$.byId[id]?.rootWorkspace`.
   */
  getWorkspaceId,
  /**
   * @deprecated Call `fieldUtils.formatFieldValueForComponentOrReference` (`@ardoq/scope-data`) with fields from `fields$`.
   */
  getFieldLabelAndValue,
  /**
   * @deprecated Call `fieldUtils.formatFieldValueForComponentOrReference` (`@ardoq/scope-data`) with fields from `fields$` and prepend the resolved name.
   */
  getNameWithFieldLabelAndValue,
  /**
   * @deprecated Call `enhancedScopeDataOperations.getComponentFields` (`@ardoq/scope-data`), or select from `fields$` with `fieldOperations` (`@ardoq/core`).
   */
  getFields,
  /**
   * @deprecated Read `components$.byId[id]?.parent`.
   */
  getParentId,
  /**
   * @deprecated Call `componentUtils.getLevel(scopeData, componentId)` (`@ardoq/scope-data`), or walk `components$.byId` upwards.
   */
  getLevel,
  /**
   * @deprecated Call `componentUtils.isExternallyManaged(scopeData, componentId)` (`@ardoq/scope-data`).
   */
  isExternallyManaged,
  /**
   * @deprecated Call `componentUtils.isExternallyMissing(scopeData, componentId)` (`@ardoq/scope-data`).
   */
  isExternallyMissing,
  /**
   * @deprecated Call `enhancedScopeDataOperations.isScopeComponent` (`@ardoq/scope-data`), or join `activeScenario$` and use `activeScenarioOperations` (`@ardoq/core`).
   */
  isScenarioContextComponent,
  /**
   * @deprecated Read `components$.byId[id]?.name`.
   */
  getDisplayName,
  /**
   * @deprecated Call `scopeDataOperations.getAncestorBreadcrumbs` (`@ardoq/scope-data`), or walk `components$.byId` upwards.
   */
  getFullPathName,
  /**
   * @deprecated Read `components$.byId[id]`.
   */
  get,
  /**
   * @deprecated Read `components$.models` and filter on `rootWorkspace`; `entityOperations.toIds` (`@ardoq/core`) if you only need the ids.
   */
  getComponentsByWorkspaceId,
  /**
   * @deprecated Call `componentOperations.canComponentHaveChildren(component, componentTypesById, isFlexible)` (`@ardoq/core`) with types from `workspaceAndModel$`.
   */
  canComponentHaveChildren,
  /**
   * @deprecated Call `componentOperations.getPossibleParents(component, workspaceComponents, descendantIds, isFlexible, componentTypesById)` (`@ardoq/core`).
   */
  getPossibleParents,
  /**
   * @deprecated Join `workspaceAndModel$` and read `scopeDataOperations.toWorkspaceWithTypes(...).componentTypes` (`@ardoq/scope-data`), or `modelOperations.getComponentTypes(model)` (`@ardoq/core`).
   */
  getPossibleTypes,
  /**
   * @deprecated Derive the diff in a reducer on `components$`; `componentOperations.buildParentChangeCascade` (`@ardoq/core`) is the pure counterpart of the cascade this triggers.
   */
  hasParentChanged,
  /**
   * @deprecated Call `componentUtils.getComponentAncestryIds` or `enhancedScopeDataOperations.getAncestorIds` (`@ardoq/scope-data`).
   */
  getComponentAncestryIds,
  /**
   * @deprecated Join `currentUserPermissionContext$` and call the pure `workspaceAccessControlInterface.canEditWorkspace(permissionContext, workspaceId, activeScenario)`.
   */
  hasWriteAccess,
  /**
   * The intention is to allow to return the attributes of e.g. Backbone
   * collections directly without creating an actual copy first. The is
   * obviously more efficient, but also more dangerous. It requires that
   * the consumer understands this and only uses this method for local
   * access to data, never spreads this data further down to other consumers
   *
   * @deprecated Read `components$.models`.
   */
  getAllComponents,
  /**
   * @deprecated Call `workspaceUtils.isWorkspaceEmpty(scopeData, workspaceId)` (`@ardoq/scope-data`), or filter `components$.models`.
   */
  isWorkspaceEmpty,
  /**
   * @deprecated Call `componentOperations.getFlatHierarchy(roots, childrenByParent)` (`@ardoq/core`) with `components$.childrenByParent`.
   */
  getComponentHierarchy,
  /**
   * @deprecated Call `componentOperations.getFlatHierarchy(roots, childrenByParent)` (`@ardoq/core`) per workspace with `components$.childrenByParent`.
   */
  getComponentsHierarchyByWorkspaceIds,
};
